import React, {Suspense} from 'react';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {endOfDay} from 'date-fns';
import {Autoplay, Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {PermissionTypeKr, RoleType} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import {buildCapitalEventUrl} from '@di-shared/features/capital/capitalMessage';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import {BrandType} from '@shared/cci/features/const/environment';
import {dateFormat, parseDateWithFormat} from '@shared/dateUtils';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';

export interface CapitalEvent1PageProps {
  vin: string;
}

/**
 * 이벤트 종료 후 제거할것 EVENT2
 *
 * @param props
 * @constructor
 */
function CapitalEvent1Page(props: CapitalEvent1PageProps) {
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_absolute"
        right={<HeaderCloseButton />}
      />
      <Suspense fallback={null}>
        <Inner {...props} />
      </Suspense>
    </div>
  );
}

export function getEvent2Url(vin: string) {
  const {getExternalVariable} = useFirebaseStore.getState();
  return buildCapitalEventUrl(getExternalVariable('CAPITAL_EVENT_URL'), vin);
}

const ERROR_MESSAGE = {
  part1: {
    [BrandType.HYUNDAI]: `대표 차량이 블루링크에 가입되어 있으며 안전운전점수 지원차량인 경우 차량의 주사용자에 한해 참여 가능합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: 마이현대 앱 ‘마이’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.KIA]: `대표 차량이 기아 커넥트에 가입되어 있으며 안전운전점수 지원차량인 경우 차량의 주사용자에 한해 참여 가능합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: Kia app ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.GENESIS]: `대표 차량이 제네시스 커넥티드 서비스에 가입되어 있으며 안전운전점수 지원차량인 경우 차량의 주사용자에 한해 참여 가능합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: MY GENESIS ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
  },
  part2: {
    [BrandType.HYUNDAI]: `대표 차량이 공유받은 차인 경우 참여가 불가합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: 마이현대 앱 ‘마이’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.KIA]: `대표 차량이 공유받은 차인 경우 참여가 불가합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: Kia app ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.GENESIS]: `대표 차량이 공유받은 차인 경우 참여가 불가합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: MY GENESIS ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
  },
};

function Inner({vin}: CapitalEvent1PageProps) {
  const brand = import.meta.env.VITE_BRAND as 'KIA' | 'HYUNDAI' | 'GENESIS';
  const {checkCapitalVinWhitelist, getExternalVariable} = useFirebaseStore(({checkCapitalVinWhitelist, getExternalVariable}) => ({
    checkCapitalVinWhitelist,
    getExternalVariable,
  }));
  const {role, getFeatureAgreed} = useAvailableComponentKr({vin});

  const eventStartDate = parseDateWithFormat(getExternalVariable('CAPITAL_EVENT_START_DATE'));
  const eventEndDate = parseDateWithFormat(getExternalVariable('CAPITAL_EVENT_END_DATE'));
  const eventPeriodText =
    eventStartDate && eventEndDate ? `${dateFormat(eventStartDate, 'M월 d일 (E)')} - ${dateFormat(eventEndDate, 'M월 d일 (E)')}` : '';
  const {openAlert} = useCcWebviewUtilsHelper();
  const {resolveError} = useApiError();
  const navigate = useNavigate();

  const isCapitalAllAgreed =
    getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT) && getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);

  const [join] = usePendingHandler(
    async () => {
      emitEventLog({
        event: 'di_vehicle_finance',
        data_action: 'event page ctr',
        data_name: '서비스 시작하고 이벤트 참여하기',
      });
      if (!(checkCapitalVinWhitelist(vin) || isEventDuration())) {
        await openAlert({
          title: '종료된 이벤트입니다.',
          message: '',
          okText: '확인',
        });
        WebviewUtils.close();
        return;
      }
      if (!isJoinable(role)) {
        const message = isEventPart2() ? ERROR_MESSAGE.part2[brand] : ERROR_MESSAGE.part1[brand];
        await openAlert({title: '이벤트 참여 대상이 아닙니다.', message});
        return;
      }

      if (!isCapitalAllAgreed) {
        await openAlert({
          title: '내 차 금융관리 서비스 가입 후 이벤트에 참여할 수 있어요!',
          message: '',
          okText: '서비스 가입하고 혜택 받을게요',
        });
        navigate(ROUTE_PATHS.CAPITAL_TERMS.getFullPath(vin) + '?from=event');
        return;
      }

      const nextScheme = `app://webView?isModalEntry=true&showNavigationBar=false&showToolbar=false&url=${encodeURIComponent(getEvent2Url(vin))}`;
      WebviewUtils.close(nextScheme);
    },
    async (error) => {
      await resolveError(error);
    }
  );

  return (
    <>
      <main
        id="content"
        className="content page_event"
      >
        <section className="section section_event">
          <span className="badge badge_container">내 차 금융관리 경품 이벤트</span>

          <h2 className="title">
            모빌리티소비 정보 확인하고 <br />
            백화점 상품권을 잡아라!
          </h2>
          <div className="date_area">
            <span className="date_text">이벤트 기간</span>
            <p className="date_info">{eventPeriodText}</p>
          </div>
          <div className="event_imgbox">
            <ImageSelector
              className=""
              alt=""
              kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_img_event_main.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_img_event_main.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_event_main_light.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_event_main.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_event_main_light.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_event_main.png')}
            />
          </div>
          <p className="event_info_desc">
            지금 바로 ‘내 차 금융관리' 서비스로
            <br />
            들어와 모빌리티소비 데이터를 확인하면
            <br />
            신세계 상품권이 내 손안에!
          </p>
        </section>
        <section className="section section_prize">
          <div className="prize_box prize_box_first">
            <div className="prize_img">
              <ImageSelector
                className=""
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_img_prize_01.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_img_prize_01.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_prize_01.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_prize_01.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_prize_01.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_prize_01.png')}
              />
            </div>
            <div className="prize_info">
              <span className="rank">1등 추첨 (1명)</span>
              <span className="prize_name">신세계 상품권 500만원</span>
            </div>
          </div>
          <div className="coupon_area">
            <div className="prize_box">
              <div className="prize_img">
                <ImageSelector
                  className=""
                  alt=""
                  kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_img_prize_02.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_img_prize_02.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_prize_02.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_prize_02.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_prize_02.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_prize_02.png')}
                />{' '}
              </div>
              <div className="prize_info">
                <span className="rank">2등 추첨 (200명)</span>
                <span className="prize_name">
                  BBQ치킨 이용권
                  <br />
                  3만원
                </span>
              </div>
            </div>
            <div className="prize_box">
              <div className="prize_img">
                <ImageSelector
                  className=""
                  alt=""
                  kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_img_prize_03.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_img_prize_03.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_prize_03.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_hyundai_img_prize_03.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_prize_03.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_genesis_img_prize_03.png')}
                />
              </div>
              <div className="prize_info">
                <span className="rank">3등 (10,000명)</span>
                <span className="prize_name">
                  GS25편의점 이용권
                  <br />
                  1만원
                </span>
              </div>
            </div>
          </div>
        </section>
        <section className="section section_event_info">
          <div className="event_heading">
            <h3 className="title">내 차 금융관리 서비스란?</h3>
            <p className="desc">
              자동차 보험, 운전자 보험, 할부금융, 리스 <br />
              그리고 모빌리티소비 정보까지 내 차의 금융 정보를
              <br />
              한번에 모아볼 수 있는 서비스예요!
            </p>
          </div>
          <Swiper
            modules={[Pagination, Autoplay]}
            className={clsx('event_service_swiper')}
            pagination={{type: 'bullets'}}
            slidesPerView="auto"
            centeredSlides
            spaceBetween={12}
            autoplay={{delay: 4000, disableOnInteraction: false}}
          >
            <SwiperSlide>
              <div className="event_info_box">
                <h4 className="title">
                  매달 지출되고 있는
                  <br />
                  모빌리티 소비를 확인하세요.
                </h4>
                <div className="event_img">
                  <ImageSelector
                    className=""
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_service_01_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_service_01.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_01_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_01.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_01_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_01.png')}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="event_info_box">
                <h4 className="title">
                  매달 지출되고 있는
                  <br />
                  모빌리티 소비를 확인하세요.
                </h4>
                <div className="event_img">
                  <ImageSelector
                    className=""
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_service_02_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_service_02.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_02_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_02.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_02_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_02.png')}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="event_info_box">
                <h4 className="title">
                  흩어져있는 내 차의 보험, 대출
                  <br />
                  자산을 연결해보세요.
                </h4>
                <div className="event_img">
                  <ImageSelector
                    className=""
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_service_03_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_service_03.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_03_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_03.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_03_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_03.png')}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="event_info_box">
                <h4 className="title">
                  내가 가입한 자동차 보험,
                  <br />
                  운전자 보험 정보와
                </h4>
                <div className="event_img">
                  <ImageSelector
                    className=""
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_service_04_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_service_04.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_04_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_04.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_04_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_04.png')}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="event_info_box">
                <h4 className="title">
                  이용 중인 할부금융, 리스의
                  <br />
                  현황과 납입내역을 확인해보세요.
                </h4>
                <div className="event_img">
                  <ImageSelector
                    className=""
                    alt=""
                    kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_service_05_light.png')}
                    kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_service_05.png')}
                    hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_05_light.png')}
                    hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_service_05.png')}
                    genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_05_light.png')}
                    genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_service_05.png')}
                  />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>

          <p className="notice_refer">
            마이데이터 기반의 자산관리 서비스와 관련 제휴 이벤트는 제휴사 현대캐피탈을 통해 운영 및 제공됩니다.
          </p>
          <p
            className="notice_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {getExternalVariable('CAPITAL_EVENT_AD_NOTICE')}
          </p>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => join()}
        >
          서비스 시작하고 이벤트 참여하기
        </button>
      </div>
    </>
  );
}

function isJoinable(role: RoleType) {
  // 원앱 홈 배포 ~ 이벤트 종료 (9/24~9/30) : case 4인 경우 참여 불가 -> case 1,2,3만 참여 가능
  return role === RoleType.NON_CCS || role === RoleType.UNREGISTERED_SPEC || role === RoleType.PRIMARY_USER;
}

export function isEventDuration() {
  const {getExternalVariable} = useFirebaseStore.getState();
  const startDate = parseDateWithFormat(getExternalVariable('CAPITAL_EVENT_START_DATE'));
  const endDate = parseDateWithFormat(getExternalVariable('CAPITAL_EVENT_END_DATE'));
  console.log('start', getExternalVariable('CAPITAL_EVENT_START_DATE'));
  console.log('end', getExternalVariable('CAPITAL_EVENT_END_DATE'));
  console.log('~~~~~~~~~~~~~~~~~~~~~~~~~~~~~\n', startDate, endDate);
  if (!startDate || !endDate) {
    return false;
  }
  const now = new Date();
  return now.getTime() >= startDate.getTime() && now.getTime() <= endOfDay(endDate).getTime();
}

function isEventPart2() {
  return new Date(2025, 8, 23, 23, 59, 59, 999).getTime() < Date.now();
}

export default CapitalEvent1Page;
